/**
 * 商品閲覧履歴機能 - localStorage ベースの実装
 * お気に入り機能と同様のデザインシステムに準拠
 */

class BrowsingHistoryManager {
  constructor() {
    this.storageKey = 'shopify_browsing_history';
    this.maxItems = 20;
    this.history = this.loadHistory();
  }

  loadHistory() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load browsing history:', e);
      return [];
    }
  }

  saveHistory() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.history));
    } catch (e) {
      console.error('Failed to save browsing history:', e);
    }
  }

  addProduct(product) {
    if (!product || !product.id) return;

    // 既存のエントリを削除（重複防止）
    this.history = this.history.filter(item => item.id !== product.id);

    // 先頭に追加
    this.history.unshift({
      id: product.id,
      handle: product.handle,
      title: product.title,
      image: product.image,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      url: product.url,
      viewedAt: new Date().toISOString()
    });

    // 最大件数を超えたら古いものを削除
    if (this.history.length > this.maxItems) {
      this.history = this.history.slice(0, this.maxItems);
    }

    this.saveHistory();
  }

  getHistory() {
    return this.history;
  }

  getCount() {
    return this.history.length;
  }

  clearHistory() {
    this.history = [];
    this.saveHistory();
  }
}

// 閲覧履歴セクション
class BrowsingHistorySection extends HTMLElement {
  constructor() {
    super();
    this.container = null;
    this.emptyMessage = null;
    this.itemsContainer = null;
  }

  connectedCallback() {
    this.container = this.querySelector('.browsing-history__container');
    this.emptyMessage = this.querySelector('.browsing-history__empty');
    this.itemsContainer = this.querySelector('.browsing-history__items');
    this.renderItems();
  }

  /** 表示件数（カスタマイザー設定。未設定・不正値は既定の4件） */
  get productsToShow() {
    const value = parseInt(this.dataset.productsToShow, 10);
    return Number.isFinite(value) && value > 0 ? value : 4;
  }

  renderItems() {
    if (!window.browsingHistoryManager) {
      window.browsingHistoryManager = new BrowsingHistoryManager();
    }

    // 履歴は新しい順に並んでいるため、先頭から必要件数だけ切り出す
    // （＝件数を超えた分は閲覧が古いものから表示されなくなる）
    const history = window.browsingHistoryManager.getHistory().slice(0, this.productsToShow);

    if (history.length === 0) {
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
      if (this.emptyMessage) this.emptyMessage.classList.remove('hidden');
      return;
    }

    if (this.emptyMessage) this.emptyMessage.classList.add('hidden');

    if (this.itemsContainer) {
      const animate = this.dataset.animateOnScroll === 'true';
      this.itemsContainer.innerHTML = history.map((item, index) => {
        const safeTitle = this.escapeHtml(item.title);
        const safeUrl = this.escapeHtml(item.url);
        const showCompare = item.compareAtPrice && item.compareAtPrice !== item.price;

        const mediaInner = item.image
          ? `<img
              src="${this.escapeHtml(item.image)}"
              alt="${safeTitle}"
              loading="lazy"
              width="533"
              height="533"
              class="motion-reduce"
            >`
          : `<div class="browsing-history__placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>`;

        return `
        <li class="browsing-history__item${animate ? ' scroll-trigger animate--slide-in' : ''}"${animate ? ` data-cascade style="--animation-order: ${index + 1};"` : ''}>
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--standard card--media" style="--ratio-percent: 100%;">
              <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 100%;">
                <div class="card__media">
                  <div class="media media--transparent media--hover-effect">
                    ${mediaInner}
                  </div>
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5">
                    <a href="${safeUrl}" class="full-unstyled-link">
                      ${safeTitle}
                    </a>
                  </h3>
                  <div class="card-information">
                    <div class="price">
                      <div class="price__container">
                        <div class="price__regular">
                          ${showCompare ? `
                            <span>
                              <s class="price-item price-item--regular">${this.escapeHtml(item.compareAtPrice)}</s>
                            </span>
                          ` : ''}
                          <span class="price-item price-item--regular">${this.escapeHtml(item.price)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>`;
      }).join('');
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// グローバルインスタンスを即時生成
// （DOMContentLoadedを待つとカスタム要素のconnectedCallbackより遅れて
//  最初の描画が空になるため、スクリプト読込時点で生成する。
//  localStorageしか使わないのでDOM完成を待つ必要はない）
if (!window.browsingHistoryManager) {
  window.browsingHistoryManager = new BrowsingHistoryManager();
}

// カスタム要素を登録
customElements.define('browsing-history-section', BrowsingHistorySection);
